// batch-61 taken-filters-status (FO-447) — DE pure overgangs-matrix van de
// operationele takenmodule. Gedeeld door de API (server-side afdwinging: reden
// verplicht, planning verplicht, toegestane overgangen) en de UI (welke snelle
// statusacties een kaart/rij/bottom-sheet toont). Geen I/O, geen Date.now().
//
// Statusmodel (CC.0b, bewust een UITBREIDING van de bestaande enum i.p.v. de
// 5-status-enum uit de spec): OPEN = BACKLOG, GEREED = UITGEVOERD; ON_HOLD en
// HERPLANNEN zijn nieuw (reden verplicht); CONTROLE_NODIG, NIET_UITGEVOERD en
// GEANNULEERD blijven bestaan omdat events en cron-regels eraan hangen.
//
// Eén bron van waarheid: de status-union en de labels komen uit @streep/types
// (dezelfde tuple als de Prisma-enum). Komt er ooit een status bij, dan dwingt
// de `Record<TaakStatus, ...>`-matrix hieronder een bewuste keuze af bij typecheck.

import {
  OPERATIONELE_TAAK_STATUS,
  OPERATIONELE_TAAK_STATUS_LABELS,
  TAAK_STATUS_REDEN_MIN,
  type OperationeleTaakStatus,
} from '@streep/types'

export const TAAK_STATUSSEN = OPERATIONELE_TAAK_STATUS
export type TaakStatus = OperationeleTaakStatus

/**
 * Open-lijst-statussen: staan default in de backoffice-backlog (te plannen of
 * te bewaken werk). ON_HOLD en HERPLANNEN horen erbij zodat een geparkeerde of
 * teruggegeven taak nooit stil uit beeld verdwijnt.
 *
 * NAAMKEUZE (batch-67 taken-engine, FO-498 §2.1, CC.0b): spec 198 noemt de
 * set "BACKLOG, CONTROLE_NODIG, GEPLAND, ON_HOLD, HERPLANNEN" `OPEN_TAAK_STATUSSEN`.
 * Die set bestaat hier al, maar heet `ACTIEVE_TAAK_STATUSSEN` (open + gepland,
 * en bovendien met NIET_UITGEVOERD, dat ook in de open lijst staat). Er is
 * bewust NIETS hernoemd: circa dertig callsites importeren deze namen. Regel:
 *   - "open lijst" (planner-werkvoorraad, zonder gepland)  -> OPEN_TAAK_STATUSSEN
 *   - "nog niet afgesloten" (tellingen, crons, offboarding,
 *     portal-acties, deadline-bewaking; de spec-lijst)      -> ACTIEVE_TAAK_STATUSSEN
 *   - "afgesloten"                                           -> AFGESLOTEN_TAAK_STATUSSEN
 * Elke status is of actief of afgesloten (bewaakt door taak-status.test.ts).
 * Een literal statuslijst in routes/services/lib is sinds batch-67 een
 * drift-fout (apps/api/src/lib/__tests__/taak-status-literal-drift.test.ts).
 */
export const OPEN_TAAK_STATUSSEN: readonly TaakStatus[] = [
  'BACKLOG',
  'CONTROLE_NODIG',
  'NIET_UITGEVOERD',
  'HERPLANNEN',
  'ON_HOLD',
]

/** Actieve statussen = open + gepland (deadline-signalen, stats, "niet afgesloten"). */
export const ACTIEVE_TAAK_STATUSSEN: readonly TaakStatus[] = [...OPEN_TAAK_STATUSSEN, 'GEPLAND']

/** Afgesloten (terminale) statussen: geen overgangen meer. */
export const AFGESLOTEN_TAAK_STATUSSEN: readonly TaakStatus[] = ['UITGEVOERD', 'GEANNULEERD']

/**
 * Statussen waarin de uitvoerder de taak daadwerkelijk "heeft" (Mijn taken,
 * badge-teller). HERPLANNEN valt er bewust buiten: die taak ligt weer bij de
 * planner tot er een nieuwe datum is.
 */
export const TOEGEWEZEN_ACTIEVE_STATUSSEN: readonly TaakStatus[] = ['GEPLAND', 'ON_HOLD']

/** Statussen waarvoor een reden verplicht is (server-side afgedwongen). */
export const REDEN_VERPLICHTE_STATUSSEN: readonly TaakStatus[] = ['ON_HOLD', 'HERPLANNEN']

/**
 * Toegestane overgangen per huidige status. GEPLAND vereist bovendien een
 * uitvoerder + geplande datum (zie `beoordeelStatusOvergang`); NIET_UITGEVOERD
 * loopt via de bestaande escalatie-service met zijn eigen reden-enum.
 *
 * batch-62 taken-ux (FO-464 §5.4): een gereed gemelde taak kan heropend worden
 * (terug naar Open), maar alleen met een reden en alleen door beheer. Binnen 24
 * uur na het gereedmelden kan de melder dat ook met één klik ongedaan maken via
 * een eigen endpoint (terug naar de vorige status); die route loopt NIET via
 * deze matrix. GEANNULEERD blijft terminaal.
 */
export const TAAK_STATUS_OVERGANGEN: Record<TaakStatus, readonly TaakStatus[]> = {
  BACKLOG: ['GEPLAND', 'ON_HOLD', 'UITGEVOERD', 'GEANNULEERD'],
  CONTROLE_NODIG: ['BACKLOG', 'GEPLAND', 'ON_HOLD', 'GEANNULEERD'],
  GEPLAND: ['UITGEVOERD', 'NIET_UITGEVOERD', 'ON_HOLD', 'HERPLANNEN', 'BACKLOG', 'GEANNULEERD'],
  ON_HOLD: ['GEPLAND', 'HERPLANNEN', 'UITGEVOERD', 'BACKLOG', 'GEANNULEERD'],
  HERPLANNEN: ['GEPLAND', 'ON_HOLD', 'BACKLOG', 'GEANNULEERD'],
  NIET_UITGEVOERD: ['GEPLAND', 'HERPLANNEN', 'ON_HOLD', 'BACKLOG', 'UITGEVOERD', 'GEANNULEERD'],
  UITGEVOERD: ['BACKLOG'],
  GEANNULEERD: [],
}

export function magOvergang(van: TaakStatus, naar: TaakStatus): boolean {
  return TAAK_STATUS_OVERGANGEN[van].includes(naar)
}

export function toegestaneOvergangen(van: TaakStatus): readonly TaakStatus[] {
  return TAAK_STATUS_OVERGANGEN[van]
}

export function statusVereistReden(naar: TaakStatus): boolean {
  return REDEN_VERPLICHTE_STATUSSEN.includes(naar)
}

/**
 * batch-62 taken-ux (FO-464): vraagt deze specifieke OVERGANG een reden? Dat is
 * zo bij On hold / Herplannen (doelstatus) én bij heropenen van een gereed
 * gemelde taak (bronstatus). Gebruik deze functie in plaats van
 * `statusVereistReden` waar de huidige status bekend is.
 */
export function overgangVereistReden(van: TaakStatus, naar: TaakStatus): boolean {
  return statusVereistReden(naar) || van === 'UITGEVOERD'
}

/** GEPLAND kan alleen met een uitvoerder én een geplande datum. */
export function statusVereistPlanning(naar: TaakStatus): boolean {
  return naar === 'GEPLAND'
}

export function isOpenStatus(status: TaakStatus): boolean {
  return OPEN_TAAK_STATUSSEN.includes(status)
}

export function isAfgesloten(status: TaakStatus): boolean {
  return AFGESLOTEN_TAAK_STATUSSEN.includes(status)
}

// ─── Snelle acties per rol ─────────────────────────────────────────────────────

export type TaakActor = 'BEHEER' | 'UITVOERDER'

/**
 * Welke snelle statusacties een rol op een kaart/rij/bottom-sheet krijgt: een
 * subset van de matrix. Beheer (backoffice, OPERATIONELE_TAKEN.canEdit) krijgt
 * alle overgangen; de uitvoerder (portal, eigen taak) alleen wat hij zelf kan
 * beslissen. Inplannen (naar GEPLAND) verloopt voor beheer via de plan-modal,
 * niet via de statusknop; voor de uitvoerder is ON_HOLD -> GEPLAND "hervatten"
 * (datum en uitvoerder staan er al).
 */
const SNELLE_ACTIES: Record<TaakActor, Partial<Record<TaakStatus, readonly TaakStatus[]>>> = {
  BEHEER: {
    BACKLOG: ['ON_HOLD', 'UITGEVOERD', 'GEANNULEERD'],
    CONTROLE_NODIG: ['BACKLOG', 'ON_HOLD', 'GEANNULEERD'],
    GEPLAND: ['UITGEVOERD', 'ON_HOLD', 'HERPLANNEN', 'BACKLOG', 'GEANNULEERD'],
    ON_HOLD: ['GEPLAND', 'HERPLANNEN', 'UITGEVOERD', 'BACKLOG', 'GEANNULEERD'],
    HERPLANNEN: ['ON_HOLD', 'BACKLOG', 'GEANNULEERD'],
    NIET_UITGEVOERD: ['HERPLANNEN', 'ON_HOLD', 'BACKLOG', 'UITGEVOERD', 'GEANNULEERD'],
    // batch-62 taken-ux (FO-464 §5.4): heropenen (met reden) - de "Status
    // wijzigen"-weg na het 24-uurs ongedaan-venster.
    UITGEVOERD: ['BACKLOG'],
  },
  UITVOERDER: {
    // Toegewezen zonder datum (BACKLOG met uitvoerder): afronden mag, de rest
    // (plannen/parkeren) is planner-werk. Een BACKLOG-taak zonder uitvoerder
    // bereikt de uitvoerder nooit (own-data-guard), dus dit is veilig.
    BACKLOG: ['UITGEVOERD'],
    GEPLAND: ['UITGEVOERD', 'ON_HOLD', 'HERPLANNEN', 'NIET_UITGEVOERD'],
    ON_HOLD: ['GEPLAND', 'UITGEVOERD', 'HERPLANNEN'],
  },
}

export function snelleStatusActies(status: TaakStatus, actor: TaakActor): readonly TaakStatus[] {
  const acties = SNELLE_ACTIES[actor][status] ?? []
  // Veiligheidsnet: een snelle actie is altijd óók een matrix-overgang.
  return acties.filter((naar) => magOvergang(status, naar))
}

// ─── batch-67 taken-engine (FO-498): wat de uitvoerder zelf nog kan ───────────
//
// Afgeleid van de matrix hierboven, zodat UI en API nooit een eigen lijst
// (`status === 'GEPLAND' || status === 'ON_HOLD'`) hoeven te schrijven. Zo'n
// eigen lijst vergat de taak die ZONDER datum is toegewezen (status Open): de
// portal-taakpagina toonde daar geen Gereedmelden, terwijl de API het toestond.

/**
 * Statussen waarin de UITVOERDER zelf nog iets met zijn taak kan (minstens één
 * snelle actie): Open (toegewezen zonder datum), Gepland en On hold.
 * HERPLANNEN valt erbuiten: die taak ligt bij de planner.
 */
export const UITVOERDER_ACTIE_STATUSSEN: readonly TaakStatus[] = TAAK_STATUSSEN.filter(
  (s) => snelleStatusActies(s, 'UITVOERDER').length > 0,
)

/** Statussen van waaruit de uitvoerder zelf gereed mag melden. */
export const UITVOERDER_GEREED_STATUSSEN: readonly TaakStatus[] = TAAK_STATUSSEN.filter((s) =>
  snelleStatusActies(s, 'UITVOERDER').includes('UITGEVOERD'),
)

/** Kan de uitvoerder zelf nog iets met een taak in deze status (kaart met acties)? */
export function isOpenVoorUitvoerder(status: TaakStatus): boolean {
  return UITVOERDER_ACTIE_STATUSSEN.includes(status)
}

/** Knop-/menulabel van een statusactie; context-afhankelijk waar dat leest. */
export function statusActieLabel(van: TaakStatus, naar: TaakStatus): string {
  if (naar === 'GEPLAND') return van === 'ON_HOLD' ? 'Hervatten' : 'Inplannen'
  switch (naar) {
    case 'UITGEVOERD':
      return 'Gereed'
    case 'ON_HOLD':
      return 'On hold'
    case 'HERPLANNEN':
      return 'Herplannen'
    case 'NIET_UITGEVOERD':
      return 'Niet uitgevoerd'
    case 'BACKLOG':
      // batch-62 (FO-464): terug vanuit Gereed heet "Heropenen".
      return van === 'UITGEVOERD' ? 'Heropenen' : 'Terug naar open'
    case 'GEANNULEERD':
      return 'Annuleren'
    case 'CONTROLE_NODIG':
      return 'Controle nodig'
  }
}

// ─── Beoordeling van één overgang (pure beslissing) ───────────────────────────

export interface StatusOvergangInput {
  van: TaakStatus
  naar: TaakStatus
  /** Vrije reden (ON_HOLD/HERPLANNEN) of null. */
  reden?: string | null
  heeftUitvoerder: boolean
  heeftGeplandeDatum: boolean
  /** Aantal nog-open checklist-items (waarschuwing bij UITGEVOERD, geen blokkade). */
  openChecklistItems?: number
}

export type StatusOvergangUitkomst =
  | { ok: true; waarschuwingen: string[] }
  | { ok: false; veld: 'status' | 'reden' | 'geplandeDatum'; melding: string }

/** Minimale redenlengte (gedeeld met het Zod-schema in @streep/types). */
export const REDEN_MIN_LENGTE = TAAK_STATUS_REDEN_MIN

/**
 * Beoordeelt één statusovergang. De API gebruikt de uitkomst voor een inline
 * veldfout (`veld`), de UI om dezelfde regel vóór verzenden te tonen. Een
 * open checklist bij afronden is een waarschuwing, geen blokkade (spec §5.2).
 */
export function beoordeelStatusOvergang(input: StatusOvergangInput): StatusOvergangUitkomst {
  const { van, naar } = input
  if (van === naar) {
    return { ok: false, veld: 'status', melding: 'De taak staat al op deze status' }
  }
  if (!magOvergang(van, naar)) {
    // Zichtbare tekst: spec-labels, geen rauwe enum-keys.
    return {
      ok: false,
      veld: 'status',
      melding: `Overgang van ${OPERATIONELE_TAAK_STATUS_LABELS[van]} naar ${OPERATIONELE_TAAK_STATUS_LABELS[naar]} is niet toegestaan`,
    }
  }
  const reden = input.reden?.trim() ?? ''
  if (overgangVereistReden(van, naar) && reden.length < REDEN_MIN_LENGTE) {
    return {
      ok: false,
      veld: 'reden',
      melding:
        naar === 'ON_HOLD'
          ? 'Reden is verplicht bij On hold'
          : naar === 'HERPLANNEN'
            ? 'Reden is verplicht bij Herplannen'
            : 'Reden is verplicht bij heropenen',
    }
  }
  if (statusVereistPlanning(naar) && (!input.heeftUitvoerder || !input.heeftGeplandeDatum)) {
    return {
      ok: false,
      veld: 'geplandeDatum',
      melding: 'Kies een medewerker en een datum om de taak op Gepland te zetten',
    }
  }
  const waarschuwingen: string[] = []
  const open = input.openChecklistItems ?? 0
  if (naar === 'UITGEVOERD' && open > 0) {
    waarschuwingen.push(
      open === 1
        ? 'Er staat nog 1 checklist-item open.'
        : `Er staan nog ${open} checklist-items open.`,
    )
  }
  return { ok: true, waarschuwingen }
}

// ─── Mijn taken: sortering + groepering (portal-startscherm) ───────────────────

/** Minimale rij-vorm voor de startscherm-groepering. */
export interface MijnTaakRij {
  status: TaakStatus
  /** 'yyyy-mm-dd' of null. */
  geplandeDatum: string | null
  /** 'yyyy-mm-dd' of null. */
  deadline: string | null
  /** 'HH:mm' of null. */
  tijdVan: string | null
  volgorde: number
}

export interface MijnTakenGroepen<T extends MijnTaakRij> {
  /** Gepland vóór vandaag en nog open (achterstallig). */
  verlopen: T[]
  vandaag: T[]
  /** Gepland ná vandaag t/m het einde van de week. */
  dezeWeek: T[]
  /** Toegewezen zonder geplande datum. */
  zonderDatum: T[]
  /** Later dan deze week (alleen als de bron ze meelevert). */
  later: T[]
}

const NA_ALLES = '9999-99-99'

/**
 * Sorteert op deadline (oplopend, zonder deadline achteraan), daarna geplande
 * datum, tijdvenster en volgorde. Deadline eerst omdat het startscherm "op
 * deadline gesorteerd" toont (spec §2.5); geen deadline -> plandatum bepaalt.
 */
export function sorteerMijnTaken<T extends MijnTaakRij>(taken: readonly T[]): T[] {
  return [...taken].sort((a, b) => {
    const da = a.deadline ?? NA_ALLES
    const db = b.deadline ?? NA_ALLES
    if (da !== db) return da < db ? -1 : 1
    const ga = a.geplandeDatum ?? NA_ALLES
    const gb = b.geplandeDatum ?? NA_ALLES
    if (ga !== gb) return ga < gb ? -1 : 1
    const ta = a.tijdVan ?? '99:99'
    const tb = b.tijdVan ?? '99:99'
    if (ta !== tb) return ta < tb ? -1 : 1
    return a.volgorde - b.volgorde
  })
}

/**
 * Groepeert toegewezen open taken voor het startscherm-blok. `vandaag` en
 * `weekTot` zijn 'yyyy-mm-dd' (NL-kalenderdag; `weekTot` = de zondag van de
 * lopende ISO-week). Elke groep is gesorteerd via `sorteerMijnTaken`.
 */
export function groepeerMijnTaken<T extends MijnTaakRij>(
  taken: readonly T[],
  vandaag: string,
  weekTot: string,
): MijnTakenGroepen<T> {
  const g: MijnTakenGroepen<T> = { verlopen: [], vandaag: [], dezeWeek: [], zonderDatum: [], later: [] }
  for (const t of sorteerMijnTaken(taken)) {
    if (!t.geplandeDatum) g.zonderDatum.push(t)
    else if (t.geplandeDatum < vandaag) g.verlopen.push(t)
    else if (t.geplandeDatum === vandaag) g.vandaag.push(t)
    else if (t.geplandeDatum <= weekTot) g.dezeWeek.push(t)
    else g.later.push(t)
  }
  return g
}
